// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * render.ts — canvas painting for a BOARDLESS field.
 *
 * Two things here are correctness rather than polish:
 *
 *  1. **The layout is computed outside the draw call.** `computeLayout()` is a
 *     pure function of (cells, targets, viewport) and both the renderer and the
 *     hit-tester call it. Sporeline computed its cell size inside the rAF draw,
 *     so every tap before the first frame — and every tap in a throttled or
 *     hidden tab — was silently dropped, with a normal-looking board and no
 *     error. It hid from automation because any check that screenshots first
 *     fires a frame and makes it vanish.
 *  2. **The camera auto-fits.** The colony grows in any direction forever, so
 *     there is no fixed board to scale to. The view fits the union of every
 *     piece and every legal target each frame, which means a phone never needs
 *     to pan and no mode can overflow its viewport.
 */

import { type GameState, type Piece, pressureOn, ringNeeded, type Seat } from './game';
import { type Hex, type HexKey, key, parseKey, toPixel } from './hex';
import { PALETTE, SEAT_COLOUR, SEAT_DEEP } from './palette';
import type { PieceType } from './modes';

export interface Layout {
  /** Hex circumradius in CSS pixels. */
  size: number;
  /** Pixel offset of hex (0,0)'s centre. */
  ox: number;
  oy: number;
  w: number;
  h: number;
}

const MIN_SIZE = 13;
const MAX_SIZE = 44;
const PAD = 10;

/**
 * Fit every interesting cell into the viewport.
 *
 * Computed in unit-hex space first, then scaled, so the fit does not depend on
 * the size it is trying to find.
 */
export function computeLayout(cells: Iterable<HexKey>, extra: Hex[], w: number, h: number): Layout {
  // A zero-size measurement is a transient during layout, not a real viewport.
  // Scaling from it yields NaN/Infinity world coordinates and silently drops
  // every subsequent tap, so fall back rather than compute from it.
  if (!(w > 0) || !(h > 0)) return { size: MIN_SIZE, ox: 0, oy: 0, w: 0, h: 0 };

  const pts: Hex[] = [...extra];
  for (const k of cells) pts.push(parseKey(k));
  if (pts.length === 0) pts.push({ q: 0, r: 0 });

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of pts) {
    const { x, y } = toPixel(p, 1);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  // A hex of unit circumradius is √3 wide and 2 tall; pad by one full hex plus
  // a ring, so a piece on the edge is never flush against the viewport.
  const unitW = maxX - minX + Math.sqrt(3) * 2.4;
  const unitH = maxY - minY + 2 * 2.0;

  const size = Math.max(
    MIN_SIZE,
    Math.min(MAX_SIZE, (w - PAD * 2) / unitW, (h - PAD * 2) / unitH),
  );
  const cx = ((minX + maxX) / 2) * size;
  const cy = ((minY + maxY) / 2) * size;
  return { size, ox: w / 2 - cx, oy: h / 2 - cy, w, h };
}

export function hexCentre(l: Layout, hx: Hex): { x: number; y: number } {
  const p = toPixel(hx, l.size);
  return { x: p.x + l.ox, y: p.y + l.oy };
}

/** Inverse — which hex is under this CSS-pixel point. */
export function hexAt(l: Layout, x: number, y: number): Hex {
  const px = x - l.ox;
  const py = y - l.oy;
  const r = (2 / 3) * (py / l.size);
  const q = px / (l.size * Math.sqrt(3)) - r / 2;
  return roundHex(q, r);
}

function roundHex(qf: number, rf: number): Hex {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q, r };
}

function hexPath(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 90);
    const px = x + size * Math.cos(a);
    const py = y + size * Math.sin(a);
    i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
  }
  ctx.closePath();
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  colour: string;
}

export interface ViewState {
  game: GameState;
  /** Seat the local player controls, or null when spectating. */
  seat: Seat | null;
  selected: Hex | null;
  /** Legal destinations for the current selection. */
  targets: Hex[];
  /** Hand piece currently picked up, if any. */
  held: PieceType | null;
  /** Cells the Sporeling could convert, awaiting a choice. */
  convertChoices: Hex[];
  /** A move being animated: from → to, progress 0..1. */
  anim: { from: Hex; to: Hex; piece: Piece; t: number } | null;
  particles: Particle[];
  /** Set false under prefers-reduced-motion. */
  motion: boolean;
  shake: number;
}

const GLYPH: Record<PieceType, string> = {
  heartroot: '',
  creeper: 'C',
  burrower: 'B',
  spindle: 'S',
  warden: 'W',
  sporeling: 'o',
};

export function draw(ctx: CanvasRenderingContext2D, l: Layout, v: ViewState): void {
  const { game } = v;
  ctx.save();
  ctx.clearRect(0, 0, l.w, l.h);
  ctx.fillStyle = PALETTE.ground;
  ctx.fillRect(0, 0, l.w, l.h);

  if (v.shake > 0 && v.motion) {
    ctx.translate((Math.random() - 0.5) * v.shake, (Math.random() - 0.5) * v.shake);
  }

  // Legal targets sit UNDER the pieces so a marker never hides a piece.
  for (const t of v.targets) {
    const { x, y } = hexCentre(l, t);
    hexPath(ctx, x, y, l.size * 0.94);
    ctx.fillStyle = PALETTE.soil;
    ctx.fill();
    ctx.strokeStyle = PALETTE.target;
    ctx.lineWidth = Math.max(2, l.size * 0.08);
    ctx.setLineDash([l.size * 0.28, l.size * 0.22]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  for (const c of v.convertChoices) {
    const { x, y } = hexCentre(l, c);
    hexPath(ctx, x, y, l.size * 1.02);
    ctx.strokeStyle = PALETTE.reaching;
    ctx.lineWidth = Math.max(3, l.size * 0.12);
    ctx.stroke();
  }

  const animKey = v.anim ? key(v.anim.from) : null;
  for (const [k, piece] of game.cells) {
    if (k === animKey) continue; // drawn at its interpolated position below
    drawPiece(ctx, l, parseKey(k), piece, v);
  }

  if (v.anim) {
    const a = hexCentre(l, v.anim.from);
    const b = hexCentre(l, v.anim.to);
    const t = easeOutCubic(v.anim.t);
    drawPieceAt(ctx, l, a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, v.anim.piece, l.size, false);
  }

  drawCollars(ctx, l, game);

  if (v.selected) {
    const { x, y } = hexCentre(l, v.selected);
    hexPath(ctx, x, y, l.size * 1.06);
    ctx.strokeStyle = PALETTE.select;
    ctx.lineWidth = Math.max(2.5, l.size * 0.1);
    ctx.stroke();
  }

  for (const p of v.particles) {
    const a = p.life / p.max;
    ctx.globalAlpha = Math.max(0, a);
    ctx.fillStyle = p.colour;
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(1.5, l.size * 0.1 * a), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawPiece(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  hx: Hex,
  piece: Piece,
  v: ViewState,
): void {
  const { x, y } = hexCentre(l, hx);
  const isSel = v.selected && v.selected.q === hx.q && v.selected.r === hx.r;
  drawPieceAt(ctx, l, x, y, piece, l.size, !!isSel);
}

function drawPieceAt(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  x: number,
  y: number,
  piece: Piece,
  size: number,
  _sel: boolean,
): void {
  const fill = SEAT_COLOUR[piece.owner];
  hexPath(ctx, x, y, size * 0.95);
  ctx.fillStyle = fill;
  ctx.fill();

  // The deep tone is an INNER bevel only — it is never painted against the
  // board, which is why the contrast gate holds it against its own fill rather
  // than against the ground.
  hexPath(ctx, x, y, size * 0.68);
  ctx.strokeStyle = SEAT_DEEP[piece.owner];
  ctx.lineWidth = Math.max(1.5, size * 0.09);
  ctx.stroke();

  if (piece.type === 'heartroot') {
    // A ring of roots, so the target of the whole game reads at a glance.
    ctx.beginPath();
    ctx.arc(x, y, size * 0.34, 0, Math.PI * 2);
    ctx.fillStyle = PALETTE.heart;
    ctx.fill();
    ctx.lineWidth = Math.max(1.5, size * 0.08);
    ctx.strokeStyle = PALETTE.heart;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * size * 0.34, y + Math.sin(a) * size * 0.34);
      ctx.lineTo(x + Math.cos(a) * size * 0.62, y + Math.sin(a) * size * 0.62);
      ctx.stroke();
    }
    return;
  }

  // Colour is never the only channel: every piece type carries a glyph.
  ctx.fillStyle = PALETTE.glyph;
  ctx.font = `700 ${Math.round(size * 0.78)}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(GLYPH[piece.type], x, y + size * 0.03);
  void l;
}

/**
 * The pressure collar — the game's real HUD.
 *
 * Each Heartroot wears a six-segment ring showing how many of its cells are
 * filled against how many will choke it RIGHT NOW, which is what makes the
 * wither visible instead of a hidden rule.
 */
function drawCollars(ctx: CanvasRenderingContext2D, l: Layout, game: GameState): void {
  for (const seat of [0, 1] as Seat[]) {
    let hr: Hex | null = null;
    for (const [k, p] of game.cells) {
      if (p.type === 'heartroot' && p.owner === seat) hr = parseKey(k);
    }
    if (!hr) continue;
    const need = ringNeeded(game.mode, game.ply, seat);
    const have = pressureOn(game, seat);
    const { x, y } = hexCentre(l, hr);
    const rad = l.size * 1.16;
    const crit = have >= need - 1;
    for (let i = 0; i < need; i++) {
      const a0 = (Math.PI * 2 * i) / need - Math.PI / 2 + 0.06;
      const a1 = (Math.PI * 2 * (i + 1)) / need - Math.PI / 2 - 0.06;
      ctx.beginPath();
      ctx.arc(x, y, rad, a0, a1);
      ctx.lineWidth = Math.max(2.5, l.size * 0.13);
      ctx.strokeStyle =
        i < have ? (crit ? PALETTE.pressureCrit : PALETTE.pressure) : PALETTE.panelEdge;
      ctx.stroke();
    }
  }
}

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

export function spawnBurst(
  out: Particle[],
  x: number,
  y: number,
  colour: string,
  n = 12,
): void {
  for (let i = 0; i < n; i++) {
    const a = (Math.PI * 2 * i) / n + Math.random() * 0.5;
    const sp = 40 + Math.random() * 90;
    out.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, max: 1, colour });
  }
}

export function stepParticles(ps: Particle[], dt: number): void {
  for (const p of ps) {
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vy += 120 * dt;
    p.life -= dt * 1.6;
  }
  for (let i = ps.length - 1; i >= 0; i--) if (ps[i].life <= 0) ps.splice(i, 1);
}
